import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Menu, X, Search } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import PaymentMethodsBanner from './PaymentMethodsBanner';
import { getCategories, getHomeSettings } from '../lib/api';

const DEFAULT_CATEGORY_LINKS = [
  { nombre: 'Hombre', link: '/catalogo/hombre' },
  { nombre: 'Mujer', link: '/catalogo/mujer' },
  { nombre: 'Accesorios', link: '/catalogo/accesorios' },
];

const Header = ({ carritoCount = 0 }) => {
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [topBannerText, setTopBannerText] = useState(
    'Compra a cuotas con Sistecredito | Envio gratis desde $200.000 | Recibe un cupon del 5% en compras superiores a $200.000',
  );
  const [creditPromoText, setCreditPromoText] = useState('Compralo hoy y pagalo luego a credito');
  const [paymentMethodsExtra, setPaymentMethodsExtra] = useState([]);
  const [categoryLinks, setCategoryLinks] = useState(DEFAULT_CATEGORY_LINKS);
  const navigate = useNavigate();
  const location = useLocation();
  const categorias = [
    ...categoryLinks,
    { nombre: 'Ofertas', link: '/ofertas' },
    { nombre: 'Nosotros', link: '/nosotros' },
  ];
  const promoMessages = useMemo(() => {
    const fromTop = String(topBannerText || '')
      .split('|')
      .map((item) => item.trim())
      .filter(Boolean);
    const fromCredit = String(creditPromoText || '').trim();
    const merged = fromCredit ? [...fromTop, fromCredit] : fromTop;
    return [...new Set(merged)];
  }, [topBannerText, creditPromoText]);
  const promoMarqueeMessages = useMemo(
    () =>
      Array.from({ length: 8 }, () => promoMessages)
        .flat()
        .filter(Boolean),
    [promoMessages],
  );

  const handleBuscar = (e) => {
    e.preventDefault();
    if (busqueda.trim()) {
      navigate(`/catalogo/todos?search=${encodeURIComponent(busqueda.trim())}`);
    }
  };

  const isActiveLink = (link) => {
    if (link === '/') {
      return location.pathname === '/';
    }
    if (link.startsWith('/catalogo/')) {
      return location.pathname.startsWith(link);
    }
    return location.pathname === link;
  };

  useEffect(() => {
    let isMounted = true;
    getCategories()
      .then((data) => {
        if (!isMounted) return;
        if (!Array.isArray(data)) return;
        const mapped = data
          .filter((item) => item && item.slug && item.name)
          .map((item) => ({
            nombre: item.name,
            link: `/catalogo/${item.slug}`,
          }));
        setCategoryLinks(mapped);
      })
      .catch(() => {});
    getHomeSettings()
      .then((data) => {
        if (!isMounted) return;
        const nextText = data?.top_banner_text;
        if (typeof nextText === 'string' && nextText.trim()) {
          setTopBannerText(nextText.trim());
        }
        const nextPromoText = data?.credit_promo_text;
        if (typeof nextPromoText === 'string' && nextPromoText.trim()) {
          setCreditPromoText(nextPromoText.trim());
        }
        const nextPaymentImages = Object.values(data?.payment_images || {})
          .filter(Boolean)
          .map((image, index) => ({
            name: `Metodo personalizado ${index + 1}`,
            logo: image,
          }));
        setPaymentMethodsExtra(nextPaymentImages);
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <header className="bg-white shadow-sm">
      {/* Banner superior */}
      <div className="bg-black text-white border-b border-white/15">
        <div className="promo-text-marquee text-sm">
          <div className="promo-text-track">
            {promoMarqueeMessages.map((message, index) => (
              <span key={`${message}-${index}`} className="promo-text-item">
                {message}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Banner metodos de pago */}
      {!location.pathname.startsWith('/admin') && (
        <PaymentMethodsBanner extraMethods={paymentMethodsExtra} />
      )}

      {/* Header principal */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="text-2xl font-bold text-black">
            Eclipse Tenis
          </Link>

          {/* Búsqueda - Desktop */}
          <form onSubmit={handleBuscar} className="hidden md:flex flex-1 max-w-md mx-8">
            <div className="relative w-full">
              <Input
                type="text"
                placeholder="Buscar zapatillas..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="w-full pr-10"
              />
              <Button
                type="submit"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full"
              >
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </form>

          {/* Iconos de acción */}
          <div className="flex items-center gap-4">
            <Link to="/carrito" className="relative">
              <Button variant="ghost" size="icon">
                <ShoppingCart className="h-5 w-5" />
                {carritoCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-black text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {carritoCount}
                  </span>
                )}
              </Button>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMenuAbierto(!menuAbierto)}
            >
              {menuAbierto ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Búsqueda - Mobile */}
        <form onSubmit={handleBuscar} className="mt-4 md:hidden">
          <div className="relative">
            <Input
              type="text"
              placeholder="Buscar zapatillas..."
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              className="w-full pr-10"
            />
            <Button
              type="submit"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full"
            >
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </form>
      </div>

      {/* Navegación - Desktop */}
      <nav className="hidden md:block border-t">
        <div className="container mx-auto px-4">
          <ul className="flex items-center justify-center gap-8 py-3">
            {categorias.map((cat) => (
              <li key={cat.nombre}>
                <Link
                  to={cat.link}
                  className={`text-sm font-medium transition-colors px-3 py-1 rounded-full ${
                    isActiveLink(cat.link)
                      ? "bg-black text-white shadow-sm"
                      : "text-gray-700 hover:text-gray-900"
                  }`}
                >
                  {cat.nombre}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Menú móvil */}
      {menuAbierto && (
        <nav className="md:hidden border-t bg-white">
          <ul className="container mx-auto px-4 py-4 space-y-3">
            {categorias.map((cat) => (
              <li key={cat.nombre}>
                <Link
                  to={cat.link}
                  className={`block py-2 text-sm font-medium transition-colors rounded-lg px-3 ${
                    isActiveLink(cat.link)
                      ? "bg-gray-100 text-gray-900"
                      : "text-gray-700 hover:text-gray-600"
                  }`}
                  onClick={() => setMenuAbierto(false)}
                >
                  {cat.nombre}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </header>
  );
};

export default Header;
